import React, { useState } from 'react';
import { Table, Badge, Select } from '@crisellevaldez/speculo-ui';
import { HiGlobe, HiSearch } from 'react-icons/hi';
import { HiDevicePhoneMobile, HiComputerDesktop, HiDeviceTablet } from 'react-icons/hi2';
import { Mail, CheckCircle, XCircle } from 'lucide-react';
import { formatDate } from '../../../../utils/dateTime';
import { OffsetPagination } from '../../../common/OffsetPagination';
import { TableCard } from '../../../common/TableCard';
import { ActivityTableHeader } from './ActivityTableHeader';
import { getWebActivityEventTypeStyle } from '../../../../utils/badgeStyles';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { stripTrackingParams } from '../../../../utils/urlUtils';
import { useContactWebActivityGQL } from '../../../../hooks/graphql/contacts/useContactWebActivityGQL';
import { WEB_ACTIVITY_TABLE_EVENT_TYPES } from './webActivityEventTypes';
import { isPreviewContactId, PREVIEW_WEB_ACTIVITY } from '../../../../pages/previewMockData';
import type { GQLWebActivityItem } from '../../../../hooks/graphql/contacts/useContact';

interface WebsiteTabProps {
  contactId: string;
  firebaseUid?: string;
  isActive?: boolean;
}

function tryParseJSON(str: string | null | undefined): Record<string, unknown> | null {
  if (!str) return null;
  try { return JSON.parse(str); } catch { return null; }
}

const getColumns = (_websiteDomain: string, _orgId: string) => [
  {
    header: 'Logged-In',
    key: 'logged_in_status',
    width: '220px',
    cell: (item: GQLWebActivityItem) => {
      const isLoggedIn = !item.isGuest;
      const guest = tryParseJSON(item.guestInfo);
      const hasGuestData = guest && (guest.firstName || guest.lastName || guest.phoneNumber || guest.email);
      const deviceTypes: Record<string, { label: string; icon: React.ReactNode }> = {
        mobile: { label: 'Mobile', icon: <HiDevicePhoneMobile className="h-5 w-5 text-primary" /> },
        tablet: { label: 'Tablet', icon: <HiDeviceTablet className="h-5 w-5 text-primary" /> },
        desktop: { label: 'Desktop', icon: <HiComputerDesktop className="h-5 w-5 text-primary" /> },
      };
      const deviceInfo = deviceTypes[item.deviceType || ''] || { label: item.deviceType || 'Unknown', icon: <HiGlobe className="h-5 w-5 text-primary" /> };
      return (
        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <div title="Logged In"><CheckCircle className="w-4 h-4 icon-green-check" /></div>
          ) : (
            <div title="Guest"><XCircle className="w-4 h-4 text-muted-foreground" /></div>
          )}
          {!isLoggedIn && hasGuestData && (
            <Badge className="badge-blue items-center gap-1">
              <Mail className="w-3 h-3" />from eAlert
            </Badge>
          )}
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            {deviceInfo.icon}
            {deviceInfo.label}
          </span>
        </div>
      );
    },
  },
  {
    header: 'Page URL',
    key: 'page_url',
    width: '300px',
    cell: (item: GQLWebActivityItem) => {
      const eventType = item.eventType;
      const isPropertyEvent = eventType === 'property_view' || eventType === 'property_saved' || eventType === 'lead_capture';
      const isSearchEvent = eventType === 'search';
      const properties = tryParseJSON(item.properties);
      const hasProperties = properties && typeof properties === 'object';
      let propertyInfo = '';
      if (isPropertyEvent && hasProperties) {
        const address = properties.address || '';
        const city = properties.city || '';
        const state = properties.state || '';
        const zipCode = properties.zipCode || '';
        if (address && city) propertyInfo = `${address}, ${city}${state ? `, ${state}` : ''}${zipCode ? ` ${zipCode}` : ''}`;
      }
      let searchLabel = '';
      if (isSearchEvent && hasProperties) {
        const searchTerm = (properties.searchQuery as string | undefined) || '';
        if (searchTerm) {
          searchLabel = searchTerm;
        } else {
          const searchFilters = properties.searchFilters as Record<string, unknown> | null | undefined;
          if (searchFilters?.filters) {
            const filters = searchFilters.filters as Record<string, unknown>;
            const location = searchFilters.location as Record<string, unknown> | null | undefined;
            const parts: string[] = [];
            if (filters.min_price) parts.push(`$${(filters.min_price as number).toLocaleString()}+`);
            if (filters.property_sub_type) parts.push(filters.property_sub_type as string);
            if (filters.standard_status) parts.push(filters.standard_status as string);
            if (location?.city) parts.push(location.city as string);
            if (location?.state) parts.push(location.state as string);
            searchLabel = parts.join(', ');
          }
        }
      }
      const cleanedUrl = stripTrackingParams(item.pageUrl || '');
      return (
        <div className="max-w-[400px]">
          {isPropertyEvent && hasProperties && propertyInfo ? (
            <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline truncate block overflow-hidden text-ellipsis" title={propertyInfo}>{propertyInfo}</a>
          ) : isSearchEvent && searchLabel ? (
            <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1.5 truncate overflow-hidden" title={searchLabel}>
              <HiSearch className="h-4 w-4 flex-shrink-0" /><span className="truncate">{searchLabel}</span>
            </a>
          ) : (
            <a href={cleanedUrl} target="_blank" rel="noopener noreferrer" className="underline truncate block overflow-hidden text-ellipsis" title={cleanedUrl}>{cleanedUrl}</a>
          )}
        </div>
      );
    },
  },
  {
    header: 'Event Type',
    key: 'event_type',
    width: '290px',
    cell: (item: GQLWebActivityItem) => {
      const eventCategory = item.eventCategory || '';
      let style;
      if (item.eventType === 'property_view') {
        if (item.pageUrl?.includes('valuation') || eventCategory === 'Valuation') {
          style = { background: 'web-evt-property-seller', text: '' };
        } else {
          style = { background: 'web-evt-property-buyer', text: '' };
        }
      } else {
        style = getWebActivityEventTypeStyle(item.eventType, eventCategory);
      }
      let label = item.eventType ? item.eventType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'Unknown';
      const properties = tryParseJSON(item.properties);
      if (item.eventType === 'property_view' && properties) {
        if (eventCategory === 'Property') label = 'Property View - Buyer';
        else if (eventCategory === 'Valuation') label = 'Home Valuation - Seller';
        else if (item.pageUrl?.includes('valuation')) label = 'Home Valuation - Seller';
        else label = 'Property View - Buyer';
      }
      return <Badge className={style.background + ' ' + style.text}>{label}</Badge>;
    },
  },
  {
    header: 'Date/Time',
    key: 'timestamp',
    width: '350px',
    cell: (item: GQLWebActivityItem) => {
      const { organization } = useOrganizationStore.getState();
      return formatDate(item.timestamp ?? '', organization?.timezone);
    },
  },
];

const eventTypeOptions = [
  { value: '', label: 'All Events' },
  { value: 'property_view', label: 'All Property Views' },
  { value: 'property_saved', label: 'Saved Property' },
  { value: 'search', label: 'Search' },
  { value: 'lead_capture', label: 'Lead Capture' },
];

export const WebsiteTab: React.FC<WebsiteTabProps> = ({ contactId }) => {
  const { organization } = useOrganizationStore();
  const { data: tenant } = useTenant(organization?.id);
  const websiteDomain = tenant?.domain?.primary || '';
  const orgId = organization?.id || '';

  const [selectedEventType, setSelectedEventType] = useState<string>('');

  const isPreview = isPreviewContactId(contactId);
  const live = useContactWebActivityGQL(
    isPreview ? '' : contactId,
    // Exclude page_view and custom events by default — match dashboard behavior
    selectedEventType ? [selectedEventType] : [...WEB_ACTIVITY_TABLE_EVENT_TYPES]
  );
  const items: GQLWebActivityItem[] = isPreview ? PREVIEW_WEB_ACTIVITY : live.items;
  const total = isPreview ? PREVIEW_WEB_ACTIVITY.length : live.total;
  const loading = isPreview ? false : live.loading;
  const resetPage = live.resetPage;

  const columns = React.useMemo(() => getColumns(websiteDomain, orgId), [websiteDomain, orgId]);

  const handleEventTypeChange = (value: string | string[]) => {
    setSelectedEventType(Array.isArray(value) ? value[0] : value);
    resetPage();
  };

  return (
    <div className="flex flex-col gap-4">
      <ActivityTableHeader
        title="Website Activity"
        count={total}
        isLoading={loading}
        showDateRange={false}
      >
        <Select
          placeholder="Event type"
          options={eventTypeOptions}
          value={selectedEventType}
          onChange={handleEventTypeChange}
          className="w-full md:w-[200px]"
        />
      </ActivityTableHeader>

      <TableCard
        borderless
        className="flex-shrink-0"
        bodyClassName="min-h-[200px] max-h-[640px] overflow-y-auto scrollbar-custom"
        footer={
          !isPreview && total > 0 ? (
            <OffsetPagination
              currentPage={live.currentPage}
              totalItems={total}
              pageSize={live.pageSize}
              onNextPage={live.handleNextPage}
              onPrevPage={live.handlePrevPage}
              onPageChange={live.goToPage}
              isLoading={loading}
              label="events"
            />
          ) : null
        }
      >
        <Table
          data={items}
          columns={columns}
          keyExtractor={(item) => item.activityId}
          emptyStateText="No website activity available"
          size="md"
          loading={loading && items.length === 0}
        />
      </TableCard>
    </div>
  );
};
